/**
 * sessionValidation.js is used by AuthLayout.js (authenticated layout) to authenticate the active user. The file contains ordinary session validation, and admin session validation.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import PublicHomePage from "./pages/public/publicHomePage/PublicHomePage";

const apiUrl = process.env.REACT_APP_URL;

const withSessionValidation = (WrappedComponent) => {
  return (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);

    const checkSession = useCallback(async () => {
      try {
        const res = await axios.get(`${apiUrl}/auth/user`, {
          withCredentials: true,
        });

        if (res.data.success) {
          setAuthenticated(true);
        } else {
          setAuthenticated(false);
          navigate("/");
        }
      } catch (error) {
        setAuthenticated(false);
        navigate("/");
      } finally {
        setLoading(false);
      }
    }, [navigate]);

    useEffect(() => {
      checkSession();
    }, [checkSession, location.pathname]); // Trigger re-check on route change

    if (loading) {
      return <div>Loading...</div>;
    }

    if (!authenticated) {
      return <PublicHomePage />; // Redirect to PublicHomePage if not authenticated
    }

    return <WrappedComponent {...props} />;
  };
};

const withAdminSessionValidation = (WrappedComponent) => {
  return (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [authenticated, setAuthenticated] = useState(false);

    const checkAdminSession = useCallback(async () => {
      try {
        const res = await axios.get(`${apiUrl}/auth/admin`, {
          withCredentials: true,
        });

        if (res.data.success) {
          setAuthenticated(true);
        } else {
          setAuthenticated(false);
          navigate("/");
        }
      } catch (error) {
        setAuthenticated(false);
        navigate("/");
      } finally {
        setLoading(false);
      }
    }, [navigate]);

    useEffect(() => {
      checkAdminSession();
    }, [checkAdminSession, location.pathname]); // Trigger re-check on route change

    if (loading) {
      return <div>Loading...</div>;
    }

    if (!authenticated) {
      return <PublicHomePage />; // Redirect to PublicHomePage if not authenticated
    }

    return <WrappedComponent {...props} />;
  };
};

export { withSessionValidation, withAdminSessionValidation };
